import {
  Alert,
  AlertIcon,
  Button,
  ButtonGroup,
  Divider,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Grid,
  GridItem,
  Input,
  Link,
  Select,
  useToast,
} from "@chakra-ui/react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import { useMutation } from "react-query";
import { Link as RouterLink, useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { InferType } from "yup";

import { handleHookFormHTTPError } from "src/common/form";
import { HTTPError, useKy } from "src/common/ky";
import { YupPossiblePhoneNumber } from "src/common/phone-numbers";
import { PhoneNumberInput } from "src/common/PhoneNumbers";
import { PageContent } from "src/layout/PageContent";
import {
  PageHeader,
  PageHeaderBackLink,
  PageHeaderSubtitle,
  PageHeaderTitle,
} from "src/layout/PageHeader";
import { Loading } from "src/common/Loading";
import { DealerUserRole } from "src/common/types";

const DealerUserValidationSchema = Yup.object().shape({
  firstName: Yup.string().required().label("First Name"),
  lastName: Yup.string().required().label("Last Name"),
  email: Yup.string().required().label("Email"),
  phone: YupPossiblePhoneNumber().required().label("Phone Number"),
  role: Yup.string().required().label("Role"),
});

type DealerUser = InferType<typeof DealerUserValidationSchema>;

const DealerUserDefaults: DealerUser = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  role: "staff",
};

export const AddDealerUser = () => {
  const ky = useKy();
  const toast = useToast();
  const { id } = useParams<"id">();
  const navigate = useNavigate();

  const { control, handleSubmit, formState, setError, getValues } = useForm({
    defaultValues: DealerUserDefaults,
    resolver: yupResolver(DealerUserValidationSchema),
  });

  const { isLoading, isSuccess, isError, error, mutate } = useMutation<
    void,
    HTTPError,
    DealerUser
  >(
    async (values: DealerUser) => {
      await ky.post("dealer-users", {
        json: { ...values, dealerOrganizationId: id },
      });
    },
    {
      onSuccess: () => {
        toast({
          title: "Staff member created.",
          description: "The dealer user was created successfully.",
          status: "success",
          isClosable: true,
        });

        navigate(`/dealers/${id}/users`);
      },
      onError: handleHookFormHTTPError(setError, getValues, toast),
    }
  );

  if (isLoading && !isSuccess) {
    return <Loading />;
  }

  return (
    <PageContent>
      <PageHeader>
        <PageHeaderBackLink>
          <Link as={RouterLink} to={`/dealers/${id}/users`}>
            &#8249; Back to list
          </Link>
        </PageHeaderBackLink>
        <PageHeaderTitle>Add Dealer User</PageHeaderTitle>
        <PageHeaderSubtitle>
          Enter the new staff member details here
        </PageHeaderSubtitle>
      </PageHeader>
      <form onSubmit={handleSubmit((data) => mutate(data))}>
        <Grid gap={4}>
          <GridItem colSpan={1}>
            <Controller
              name="firstName"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl
                  isRequired
                  isInvalid={fieldState.invalid && fieldState.isTouched}
                >
                  <FormLabel htmlFor="firstName">First Name</FormLabel>
                  <Input {...field} id="firstName" />
                  <FormErrorMessage>
                    {fieldState.error?.message}
                  </FormErrorMessage>
                </FormControl>
              )}
            />
          </GridItem>

          <GridItem colSpan={1}>
            <Controller
              name="lastName"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl
                  isRequired
                  isInvalid={fieldState.invalid && fieldState.isTouched}
                >
                  <FormLabel htmlFor="lastName">Last Name</FormLabel>
                  <Input {...field} id="lastName" />
                  <FormErrorMessage>
                    {fieldState.error?.message}
                  </FormErrorMessage>
                </FormControl>
              )}
            />
          </GridItem>

          <GridItem colSpan={1}>
            <Controller
              name="email"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl
                  isRequired
                  isInvalid={fieldState.invalid && fieldState.isTouched}
                >
                  <FormLabel htmlFor="email">Email</FormLabel>
                  <Input {...field} id="email" />
                  <FormErrorMessage>
                    {fieldState.error?.message}
                  </FormErrorMessage>
                </FormControl>
              )}
            />
          </GridItem>

          <GridItem colSpan={1}>
            <Controller
              name="phone"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl
                  isRequired
                  isInvalid={fieldState.invalid && fieldState.isTouched}
                >
                  <FormLabel htmlFor="phone">Phone Number</FormLabel>
                  <PhoneNumberInput {...field} control={control} id="phone" />
                  <FormErrorMessage>
                    {fieldState.error?.message}
                  </FormErrorMessage>
                </FormControl>
              )}
            />
          </GridItem>

          <GridItem>
            <Controller
              name="role"
              control={control}
              render={({ field, fieldState }) => (
                <FormControl
                  isRequired
                  isInvalid={fieldState.invalid && fieldState.isTouched}
                >
                  <FormLabel htmlFor="role">Role</FormLabel>
                  <Select {...field} id="role">
                    <option value={DealerUserRole.admin}>Admin</option>
                    <option value={DealerUserRole.staff}>Staff</option>
                  </Select>
                  <FormErrorMessage>
                    {fieldState.error?.message}
                  </FormErrorMessage>
                </FormControl>
              )}
            />
          </GridItem>

          {isError && error && (
            <GridItem colSpan={2}>
              <Alert status="error">
                <AlertIcon />
                There was a problem adding the staff member.
              </Alert>
            </GridItem>
          )}

          <GridItem colSpan={2}>
            <Divider />
          </GridItem>

          <GridItem colSpan={2}>
            <ButtonGroup display="flex" justifyContent="flex-end">
              <Link as={RouterLink} to={`/dealers/${id}/users`}>
                <Button>Cancel</Button>
              </Link>
              <Button
                type="submit"
                colorScheme="brand.blue"
                isLoading={formState.isSubmitting}
              >
                Save
              </Button>
            </ButtonGroup>
          </GridItem>
        </Grid>
      </form>
    </PageContent>
  );
};
